import { z } from "zod";

/**
 * ORGANİZASYON / ÜYELİK / ROL AI ÖNERİLERİ (CLOS-288 `[192]` · CLOS-1013/1014).
 *
 * ### ⚠ ÖNERİ BİR YETKİ DEĞİL, BİR OKUMA
 *
 * Kartın kapsam sınırı: *"AI organizasyon türü/durumu, profil, davet, üyelik, departman,
 * rol, permission, kullanıcı ataması, kayıt erişimi veya workflow hedefi
 * oluşturmaz/değiştirmez."* Şemada bu yüzden bir "uygula" ya da "onayla" alanı yok;
 * önerilen izin değişikliği bile yalnız bir LİSTE ve o liste sunucuda kanonik yetki
 * kurallarından geçiriliyor (`blocked`).
 *
 * ### ⚠ ETKİ KAPSAMI SAYIYLA, KİMLİKLE DEĞİL
 *
 * Kart: *"etkilenen kullanıcı/işlev kapsamı"* isteniyor ama *"private kayıt ayrıntıları
 * gereksiz açılmaz"* da diyor. `affectedMembers`/`affectedInvitations` birer SAYI: "bu
 * değişiklik 4 kişiyi etkiler" cümlesi kararı vermeye yeter, o dört kişinin kim olduğu
 * ayrı bir yetki sorusudur ve rol ekranının kendi işidir.
 */

export const AI_ORG_ADVICE_CATEGORIES = [
  "excess_permission",
  "least_privilege",
  "profile_gap",
  "inactive_membership",
  "admin_continuity",
  "department",
] as const;
export type AiOrgAdviceCategory = (typeof AI_ORG_ADVICE_CATEGORIES)[number];

export const AI_ORG_ADVICE_CATEGORY_NAMES: Record<AiOrgAdviceCategory, string> = {
  excess_permission: "Fazla yetki",
  least_privilege: "Asgari yetki",
  profile_gap: "Eksik bilgi",
  inactive_membership: "Kullanılmayan üyelik",
  admin_continuity: "Yönetici sürekliliği",
  department: "Departman düzeni",
};

export const AI_ORG_ADVICE_PRIORITIES = ["high", "medium", "low"] as const;
export type AiOrgAdvicePriority = (typeof AI_ORG_ADVICE_PRIORITIES)[number];

export const AI_ORG_ADVICE_PRIORITY_NAMES: Record<AiOrgAdvicePriority, string> = {
  high: "Önce bunu inceleyin",
  medium: "İncelenmeli",
  low: "Bilgi amaçlı",
};

/**
 * ⚠ EKRANDA ZORUNLU. Yetki alanında bir AI önerisinin "karar" gibi okunması, bir gün
 * birinin yetkisini AI'ın önerdiği için değiştirmek demektir. Cümle şemada.
 */
export const AI_ORG_ADVICE_ADVISORY =
  "Bu öneriler bir yetki kararı değildir; hiçbiri kendiliğinden uygulanmaz. Değişikliği " +
  "ilgili rol, üyelik veya departman ekranından siz yaparsınız.";

export const AI_ORG_ADVICE_EMPTY_MESSAGE =
  "Bu taramada öne çıkan bir öneri çıkmadı. Bu, yetki yapınızın kusursuz olduğu anlamına " +
  "gelmez.";

/**
 * ⚠ İZİN DEĞİŞİKLİĞİ ÖNERİSİ — ROL KİMLİĞİ ve ANAHTAR LİSTESİ. Modelin serbest metinle
 * "şu yetkiyi kaldırın" demesi ölçülemez bir öneridir; anahtar listesi ise sunucuda
 * kanonik kurallardan geçirilebilir (`evaluateGrant`).
 */
export const aiOrgPermissionChangeSchema = z.strictObject({
  roleId: z.string().min(1).max(200),
  add: z.array(z.string().min(1).max(120)).max(50).default([]),
  remove: z.array(z.string().min(1).max(120)).max(50).default([]),
});
export type AiOrgPermissionChange = z.infer<typeof aiOrgPermissionChangeSchema>;

export const aiOrgAdviceSchema = z.strictObject({
  category: z.enum(AI_ORG_ADVICE_CATEGORIES),
  priority: z.enum(AI_ORG_ADVICE_PRIORITIES),
  confidence: z.enum(["high", "medium", "low"]),
  title: z.string().min(1).max(200),
  rationale: z.string().min(1).max(1500),
  /** Öneri hangi kayda bakıyor — rol adı, departman adı ya da "organizasyon profili". */
  subject: z.string().min(1).max(200),
  /** ⚠ İsteğe bağlı: her öneri bir izin değişikliği önermiyor (ör. eksik profil bilgisi). */
  permissionChange: aiOrgPermissionChangeSchema.nullish(),
});
export type AiOrgAdvice = z.infer<typeof aiOrgAdviceSchema>;

export const aiOrgAdviceOutputSchema = z.strictObject({
  advices: z.array(aiOrgAdviceSchema).max(30),
  reviewNeeded: z.array(z.string().min(1).max(300)).max(10).default([]),
});
export type AiOrgAdviceOutput = z.infer<typeof aiOrgAdviceOutputSchema>;

/**
 * SUNUCUNUN ÖNERİYE EKLEDİĞİ KANONİK KARAR.
 *
 * ### ⚠ MODELİN ÖNERİSİ, SİSTEMİN KURALINDAN GEÇMEDEN EKRANA ÇIKMIYOR
 *
 * Kart: *"Minimum yetki önerisi mevcut permission kataloğu, protected permission,
 * grantable ceiling, tek aktif rol ve organizasyon türü kurallarını AŞAMAZ."* Modelden bu
 * kuralları bilmesini istemek, onları bir yönergeye kopyalamak ve o kopyanın gün gelip
 * ayrışmasını göze almak olurdu. Bunun yerine sunucu her önerilen anahtarı KANONİK
 * `evaluateGrant` kapısından geçiriyor ve geçemeyeni `blockedPermissions`'a yazıyor —
 * ekran onu "uygulanamaz" diye gösteriyor.
 */
export type AiOrgAdviceView = AiOrgAdvice & {
  /**
   * ⚠ AI KESİN BİR İFADE KULLANDI MI (CLOS-1087). Cümle SİLİNMİYOR ya da düzeltilmiyor —
   * işaretleniyor; risk ailesinin `certaintyFlagged` kararıyla aynı gerekçe: çıktıyı
   * reddetmek kullanıcının kredisini bir kelime seçimi yüzünden yakardı, sessizce
   * düzeltmek AI'ın ne dediğini kullanıcıdan gizlerdi.
   */
  certaintyFlagged: boolean;

  /** Kanonik kapıdan geçemeyen anahtarlar; boşsa öneri uygulanabilir görünüyor. */
  blockedPermissions: { permission: string; reason: string }[];
  /** ⚠ SAYI, KİMLİK DEĞİL: "kaç kişiyi etkiler" kararı vermeye yeter. */
  affectedMembers: number;
  affectedInvitations: number;
};

export type AiOrgAdviceRunSummary = {
  id: string;
  status: "completed" | "failed";
  advices: AiOrgAdviceView[];
  reviewNeeded: string[];
  errorCode: "provider_error" | "invalid_output" | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  createdByName: string;
  createdAt: string;
};

export const AI_ORG_ADVICE_ERROR_MESSAGES: Record<"provider_error" | "invalid_output", string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; öneri üretilemedi ve krediniz iade edildi.",
  invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
};

/** ⚠ Uygulanamaz önerinin ekrandaki cümlesi — ekran kendi metnini yazmasın. */
export const AI_ORG_ADVICE_BLOCKED_WARNING =
  "Bu önerinin bazı yetkileri organizasyonunuzun yetki kurallarına takılıyor; " + "uygulanamaz.";
